import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, IconButton, Tooltip } from '@mui/material'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { alpha } from '@mui/material/styles'
import theme from '../../../themeConfig'

/**
 * ProcessTreeInspectorDock.jsx
 * The frame the inspector lives in on the right of the process Tree surface: a border of its own, a
 * gutter you can drag to make it wider, and a button that folds it away when the tree is what matters.
 *
 * It owns the chrome and nothing else — it never looks at the selected node. Whatever is handed to it as
 * children is what gets drawn inside, so the read-only contract of the surface (RN7/INV3) is untouched:
 * the only thing this dock writes is how wide it is and whether it is open, both of them per browser.
 */

/** The panel's colours are the window's: `themeConfig.js`, not the Studio's. */
const P = theme.palette
const FRAME_RADIUS = theme.tokens.radius.md + 'px'

const MIN_WIDTH = 240
const MAX_WIDTH = 900
// Story #150 (F14): the panel now hosts the graph's own task table, which needs room to be read. It
// stays resizable between the same bounds, so anyone who wants it narrow still can.
const DEFAULT_WIDTH = 640
const KEYBOARD_STEP = 24
const GUTTER_PX = 8
const RAIL_PX = 40

const STORAGE_WIDTH = 'processTree.inspector.width'
const STORAGE_OPEN = 'processTree.inspector.open'

const clampWidth = (value) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(value)))

/**
 * POL4: an unreadable preference is not an error, it is simply no preference. Private browsing and a
 * browser with site data blocked both throw on read, so every access is guarded.
 */
const readStored = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key)
    return raw === null ? fallback : raw
  } catch (error) {
    return fallback
  }
}

const writeStored = (key, value) => {
  try {
    window.localStorage.setItem(key, value)
  } catch (error) {
    // A preference that cannot be kept is not worth breaking the screen over.
  }
}

const readStoredWidth = () => {
  const parsed = Number.parseInt(readStored(STORAGE_WIDTH, ''), 10)
  return Number.isFinite(parsed) ? clampWidth(parsed) : DEFAULT_WIDTH
}

/**
 * The control that folds the panel away and brings it back. It sits at the top right of the header, which
 * is where anyone looks for it — a small disc floating on the border reads as an artefact, not a button.
 */
const DockToggle = ({ open, onToggle, label }) => (
  <Tooltip title={label}>
    <IconButton
      data-testid='process-tree-inspector-toggle'
      aria-label={label}
      aria-expanded={open}
      onClick={onToggle}
      sx={{
        width: 28,
        height: 26,
        flex: '0 0 28px',
        p: 0,
        borderRadius: '999px',
        border: '1px solid ' + P.background.color_373636,
        background: P.background.color_212121,
        color: '#bbbbbb',
        fontSize: 15,
        lineHeight: 1,
        '&:hover': { background: P.background.color_373636, color: P.primary.main }
      }}
    >
      {open ? <ChevronRightRoundedIcon sx={{ fontSize: 17 }} /> : <ChevronLeftRoundedIcon sx={{ fontSize: 17 }} />}
    </IconButton>
  </Tooltip>
)

const ProcessTreeInspectorDock = ({ children }) => {
  const { t_ } = useSmartTranslation()
  const [width, setWidth] = useState(readStoredWidth)
  const [open, setOpen] = useState(() => readStored(STORAGE_OPEN, 'true') !== 'false')
  const [dragging, setDragging] = useState(false)
  const dragRef = useRef({ startX: 0, startWidth: DEFAULT_WIDTH })

  useEffect(() => { writeStored(STORAGE_WIDTH, String(width)) }, [width])
  useEffect(() => { writeStored(STORAGE_OPEN, open ? 'true' : 'false') }, [open])

  /**
   * While the gutter is being dragged the pointer flies over the tree, and without this it selects every
   * row title on the way and flickers back to the arrow cursor. The guard is on the body because that is
   * the only element the pointer cannot leave.
   */
  useEffect(() => {
    if (!dragging) {
      return undefined
    }
    const previousSelect = document.body.style.userSelect
    const previousCursor = document.body.style.cursor
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
    return () => {
      document.body.style.userSelect = previousSelect
      document.body.style.cursor = previousCursor
    }
  }, [dragging])

  // The gutter sits on the LEFT of the panel, so dragging towards the tree makes the panel wider.
  const applyDelta = useCallback((clientX) => {
    setWidth(clampWidth(dragRef.current.startWidth + (dragRef.current.startX - clientX)))
  }, [])

  const startDrag = useCallback((event) => {
    dragRef.current = { startX: event.clientX, startWidth: width }
    setDragging(true)
    event.currentTarget.setPointerCapture?.(event.pointerId)
    event.preventDefault()
  }, [width])

  const onDrag = useCallback((event) => {
    if (!dragging) {
      return
    }
    applyDelta(event.clientX)
  }, [dragging, applyDelta])

  const endDrag = useCallback((event) => {
    if (!dragging) {
      return
    }
    setDragging(false)
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  }, [dragging])

  // The separator is reachable without a mouse: the arrows resize it, Home and End go to the extremes.
  const onKeyDown = useCallback((event) => {
    if (event.key === 'ArrowLeft') {
      setWidth((current) => clampWidth(current + KEYBOARD_STEP))
    } else if (event.key === 'ArrowRight') {
      setWidth((current) => clampWidth(current - KEYBOARD_STEP))
    } else if (event.key === 'Home') {
      setWidth(MAX_WIDTH)
    } else if (event.key === 'End') {
      setWidth(MIN_WIDTH)
    } else {
      return
    }
    event.preventDefault()
  }, [])

  // Folded away, the panel leaves a rail behind that still looks like the panel — same border, same
  // recessed header — so it reads as "this opens up again" and not as a stray button.
  if (!open) {
    return (
      <Box
        data-testid='process-tree-inspector-dock'
        data-open='false'
        sx={{
          width: RAIL_PX,
          flexShrink: 0,
          ml: 1,
          border: '1px solid ' + P.background.color_373636,
          borderRadius: FRAME_RADIUS,
          background: P.background.card,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          pt: 0.5
        }}
      >
        <DockToggle open={false} onToggle={() => setOpen(true)} label={t_('Expand')} />
      </Box>
    )
  }

  return (
    <Box
      data-testid='process-tree-inspector-dock'
      data-open='true'
      sx={{ display: 'flex', alignItems: 'stretch', flexShrink: 0 }}
    >
      <Box
        data-testid='process-tree-inspector-resizer'
        role='separator'
        aria-orientation='vertical'
        aria-label={t_('Resize')}
        aria-valuenow={width}
        aria-valuemin={MIN_WIDTH}
        aria-valuemax={MAX_WIDTH}
        tabIndex={0}
        onPointerDown={startDrag}
        onPointerMove={onDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
        sx={{
          position: 'relative',
          width: GUTTER_PX,
          flexShrink: 0,
          cursor: 'col-resize',
          touchAction: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover, &:focus-visible': { background: alpha(P.primary.main, 0.18) }
        }}
      />
      <Box
        sx={{
          position: 'relative',
          width,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          /*
           * El panel mide lo que tiene dentro, sin estirar (`flex-start`), que es lo que dejaba medio metro
           * de vacio bajo la tabla. #215 (D3): su techo es el alto de la fila que pone el usuario; pasado el,
           * desplaza el cuerpo de la pestana en vez de agrandar la fila.
           */
          alignSelf: 'flex-start',
          maxHeight: '100%',
          minHeight: 0,
          // The Studio's dock in the window's clothes: the theme's card, its border grey, its `md` radius,
          // and an elevation shadow rather than a halo — floating, but not a bubble.
          border: '1px solid ' + P.background.color_373636,
          borderRadius: FRAME_RADIUS,
          background: P.background.card,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
          overflow: 'hidden',
          // While dragging, the pointer must not select the text it flies over.
          userSelect: dragging ? 'none' : 'auto'
        }}
      >
        {/* ZoneHeader of the Studio: 34px, `bg-card-soft`, 9.5px/800/.18em accent label, round buttons. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: '34px', flex: '0 0 34px', padding: '0 10px 0 16px', background: P.background.color_212121, borderBottom: '1px solid ' + P.background.color_373636 }}>
          <Box component='span' sx={{ fontSize: '10.5px', fontWeight: 800, letterSpacing: '.18em', textTransform: 'uppercase', color: P.primary.main, fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
            {t_('Node inspector')}
          </Box>
          <Box component='span' sx={{ flexGrow: 1 }} />
          <DockToggle open onToggle={() => setOpen(false)} label={t_('Collapse')} />
        </Box>
        {children}
      </Box>
    </Box>
  )
}

export default ProcessTreeInspectorDock
